/**
 * The site survey: what the installer needs to know on the day, beyond the
 * heat loss — where the heat pump and cylinder go, the electrics, the
 * existing system, pipework, access and water, energy use and the team's own
 * notes (migration 0056).
 *
 * ONE DEFINITION, TWO USES. The API checks every answer against the field it
 * names here; the portal draws each form from the same list. A question added
 * here is asked, validated and counted in the progress at once, and the two
 * can never disagree about what a section holds.
 *
 * EASY TO ANSWER. Most questions are a tap: a choice, a yes or no. Photos are
 * picked from the ones the phone already took (`photoHints` suggest which), and
 * a question that only matters after another answer (`when`) is only asked
 * then. Required questions are the ones an installer cannot do without; they
 * are what the progress counts.
 */

export const SITE_SURVEY_SECTION_IDS = [
  "heat-pump",
  "electrics",
  "cylinder",
  "existing-heating",
  "pipework",
  "site",
  "energy",
  "notes",
] as const;
export type SiteSurveySectionId = (typeof SITE_SURVEY_SECTION_IDS)[number];

export type SiteFieldKind =
  | "choice"
  | "multi"
  | "yes-no"
  | "number"
  | "text"
  | "long-text"
  | "photos"
  | "circuits";

export interface SiteFieldOption {
  readonly value: string;
  readonly label: string;
  readonly help?: string;
}

export interface SiteField {
  readonly id: string;
  readonly label: string;
  readonly kind: SiteFieldKind;
  readonly required?: boolean;
  readonly help?: string;
  readonly options?: readonly SiteFieldOption[];
  readonly unit?: string;
  readonly min?: number;
  readonly max?: number;
  readonly maxLength?: number;
  /** Asked only when another answer in the same section is this. */
  readonly when?: { readonly field: string; readonly is: string | boolean };
  /** Not asked when another answer in the same section is this — "space heating only" skips the cylinder. */
  readonly unless?: { readonly field: string; readonly is: string | boolean };
  /** Words to look for in a phone photo's note, to suggest photos for this question. */
  readonly photoHints?: readonly string[];
}

export interface SiteGroup {
  readonly title: string;
  readonly fields: readonly SiteField[];
}

export interface SiteSection {
  readonly id: SiteSurveySectionId;
  readonly title: string;
  /** One line under the title: what the section is for. */
  readonly blurb: string;
  readonly groups: readonly SiteGroup[];
}

export const CIRCUIT_KINDS = [
  "sockets",
  "lighting",
  "cooker",
  "shower",
  "immersion",
  "heat-pump",
  "ev-charger",
  "other",
] as const;
export type CircuitKind = (typeof CIRCUIT_KINDS)[number];

export const CIRCUIT_KIND_LABELS: Readonly<Record<CircuitKind, string>> = {
  sockets: "Sockets",
  lighting: "Lighting",
  cooker: "Cooker",
  shower: "Electric shower",
  immersion: "Immersion heater",
  "heat-pump": "Heat pump",
  "ev-charger": "EV charger",
  other: "Other",
};

/** One circuit in the consumer unit, as the surveyor reads it off the board. */
export interface Circuit {
  readonly name: string;
  readonly kind: CircuitKind;
  /** The breaker's rating, A. */
  readonly ratingA: number;
  /** What it actually draws, A, when known (a datasheet, a nameplate); else the rating stands in. */
  readonly loadA?: number;
}

export type SiteAnswer = string | number | boolean | readonly string[] | readonly Circuit[];
export type SiteAnswers = Readonly<Record<string, SiteAnswer>>;

const yes = (id: string, label: string, extra: Partial<SiteField> = {}): SiteField => ({
  id,
  label,
  kind: "yes-no",
  required: true,
  ...extra,
});
const mm = (id: string, label: string): SiteField => ({
  id,
  label,
  kind: "number",
  unit: "mm",
  min: 50,
  max: 20_000,
});
const opts = (...pairs: readonly (readonly [string, string, string?])[]): readonly SiteFieldOption[] =>
  pairs.map(([value, label, help]) => ({ value, label, ...(help ? { help } : {}) }));

/** A space-heating-only job has no cylinder: every other cylinder question is skipped. */
const HEATING_ONLY = { field: "space_heating_only", is: true } as const;
const unlessHeatingOnly = (groups: readonly SiteGroup[]): SiteGroup[] =>
  groups.map((g) => ({ ...g, fields: g.fields.map((f) => ({ ...f, unless: HEATING_ONLY })) }));

const COPPER = opts(
  ["cu-10", "10 mm copper"],
  ["cu-15", "15 mm copper"],
  ["cu-22", "22 mm copper"],
  ["cu-28", "28 mm copper"],
  ["cu-35", "35 mm copper"],
);

export const SITE_SURVEY_SECTIONS: readonly SiteSection[] = [
  {
    id: "heat-pump",
    title: "Heat pump location",
    blurb: "Where the outdoor unit goes, how it is installed, and what planning asks of it.",
    groups: [
      {
        title: "Where it goes",
        fields: [
          {
            id: "hp_photos",
            label: "Photos of the spot",
            kind: "photos",
            required: true,
            photoHints: ["heat pump", "outside", "rear", "garden", "side", "back wall"],
          },
          mm("space_width_mm", "Width available"),
          mm("space_height_mm", "Height available"),
          mm("space_depth_mm", "Depth available"),
          {
            id: "location_description",
            label: "Describe the spot",
            kind: "long-text",
            required: true,
            help: "Which wall, and what is near it — windows, doors, drains, the boundary.",
          },
        ],
      },
      {
        title: "Installation",
        fields: [
          {
            id: "mounting",
            label: "Mounted on",
            kind: "choice",
            required: true,
            options: opts(["ground", "The ground"], ["wall", "Wall brackets"], ["flat-roof", "A flat roof"]),
          },
          {
            id: "base_by",
            label: "Base built by",
            kind: "choice",
            required: true,
            options: opts(["installer", "Our installer"], ["builder", "A builder"], ["existing", "Existing slab or base"]),
          },
          {
            id: "condensate",
            label: "Condensate goes to",
            kind: "choice",
            required: true,
            options: opts(["drain", "A drain or downpipe nearby"], ["soakaway", "A soakaway, to be dug"]),
          },
          { id: "condensate_photos", label: "Photos of the drain", kind: "photos", photoHints: ["drain", "gully", "downpipe"] },
        ],
      },
      {
        title: "Planning",
        fields: [
          {
            id: "planning_method",
            label: "Noise method",
            kind: "choice",
            required: true,
            options: opts(["mcs-020a", "MCS 020(a)", "The current method"], ["mcs-020", "MCS 020", "The old method"]),
          },
          yes("listed", "Listed building?"),
          yes("conservation", "In a conservation area?"),
          yes("fronts_highway", "Would it face a road?"),
          yes("above_ground", "Above ground floor level?"),
        ],
      },
    ],
  },
  {
    id: "electrics",
    title: "Electrics",
    blurb: "The supply, the earthing and the consumer unit — enough to work out the maximum demand and the DNO route.",
    groups: [
      {
        title: "Incoming supply",
        fields: [
          {
            id: "cutout_photos",
            label: "Photos of the cut-out and meter",
            kind: "photos",
            required: true,
            photoHints: ["cut out", "cut-out", "cutout", "main fuse", "meter", "supply"],
          },
          {
            id: "phase",
            label: "Phase",
            kind: "choice",
            required: true,
            options: opts(["single", "Single"], ["three", "Three"], ["split", "Split"]),
          },
          {
            id: "fuse_a",
            label: "Main fuse",
            kind: "choice",
            required: true,
            help: "Printed on the fuse carrier in the cut-out.",
            options: opts(["60", "60 A"], ["80", "80 A"], ["100", "100 A"], ["unknown", "Don't know"]),
          },
          yes("looped", "Looped supply?", { help: "Shared with next door. The DNO may need to unloop it first." }),
          yes("supply_issues", "Anything wrong with the supply?"),
          { id: "supply_notes", label: "What is wrong?", kind: "long-text", when: { field: "supply_issues", is: true } },
          yes("safety_concern", "Any safety concern with the DNO's equipment?"),
        ],
      },
      {
        title: "Earthing",
        fields: [
          {
            id: "earthing",
            label: "Earthing",
            kind: "choice",
            required: true,
            options: opts(
              ["tn-c-s", "TN-C-S (PME)", "Earth clamped to the neutral at the cut-out."],
              ["tn-s", "TN-S", "Earth taken from the supply cable's sheath."],
              ["tt", "TT (earth rod)", "A rod in the ground at the house."],
            ),
          },
          { id: "earthing_photos", label: "Photos of the earthing", kind: "photos", photoHints: ["earth"] },
        ],
      },
      {
        title: "Consumer unit",
        fields: [
          {
            id: "cu_photos",
            label: "Photos of the consumer unit",
            kind: "photos",
            required: true,
            photoHints: ["consumer", "fuse board", "fuseboard", "board", "breaker", "mcb", "rcd"],
          },
          yes("spare_way", "A spare way for the heat pump?"),
          { id: "mcb_brand", label: "Breaker make", kind: "text", maxLength: 60 },
          {
            id: "circuits",
            label: "Circuits",
            kind: "circuits",
            help: "Each circuit and its breaker. The maximum demand is worked out from these.",
          },
          { id: "measured_demand_a", label: "Measured peak (clamp meter)", kind: "number", unit: "A", min: 0, max: 400 },
        ],
      },
      {
        title: "Cable runs",
        fields: [
          { id: "cable_board_hp_m", label: "Board to heat pump", kind: "number", unit: "m", min: 0, max: 500 },
          { id: "cable_hp_cylinder_m", label: "Heat pump to cylinder", kind: "number", unit: "m", min: 0, max: 500 },
          yes("immersion_feed", "Immersion heater feed needed?", { required: false }),
        ],
      },
    ],
  },
  {
    id: "cylinder",
    title: "Cylinder & hot water",
    blurb: "Where the new cylinder goes, its discharge and the pipe run to the heat pump — or none, on a space-heating-only job.",
    groups: [
      {
        title: "Hot water",
        fields: [
          {
            id: "space_heating_only",
            label: "Space heating only — no hot water cylinder?",
            kind: "yes-no",
            required: false,
            help: "The heat pump heats the house only; hot water stays as it is. The rest of this section is then not asked.",
          },
        ],
      },
      ...unlessHeatingOnly([
      {
        title: "What is there now",
        fields: [
          yes("existing_cylinder", "Is there a hot water cylinder now?"),
          {
            id: "existing_cylinder_notes",
            label: "What is it, and where?",
            kind: "text",
            maxLength: 200,
            when: { field: "existing_cylinder", is: true },
          },
        ],
      },
      {
        title: "Where the new one goes",
        fields: [
          {
            id: "cylinder_photos",
            label: "Photos of the spot",
            kind: "photos",
            required: true,
            photoHints: ["cylinder", "airing", "cupboard", "loft", "tank"],
          },
          mm("cyl_space_width_mm", "Width available"),
          mm("cyl_space_depth_mm", "Depth available"),
          mm("cyl_space_height_mm", "Height available"),
          {
            id: "cylinder_location",
            label: "Describe the spot",
            kind: "long-text",
            required: true,
            help: "If it is going in the loft, include the hatch size.",
          },
        ],
      },
      {
        title: "Discharge and pipes",
        fields: [
          { id: "d2_route", label: "D2 discharge route", kind: "long-text", help: "Where the tundish goes and how the D2 pipe runs to a safe outlet." },
          { id: "pipe_run_m", label: "Pipe run to the heat pump", kind: "number", unit: "m", min: 0, max: 500 },
          { id: "pipe_run_route", label: "Pipe route to the heat pump", kind: "long-text" },
        ],
      },
      ]),
    ],
  },
  {
    id: "existing-heating",
    title: "Existing heating",
    blurb: "What heats the house now, and what happens to it.",
    groups: [
      {
        title: "What is there now",
        fields: [
          {
            id: "heating_photos",
            label: "Photos of the boiler or heater",
            kind: "photos",
            required: true,
            photoHints: ["boiler", "heater", "flue", "storage"],
          },
          {
            id: "fuel",
            label: "Fuel",
            kind: "choice",
            required: true,
            options: opts(
              ["mains-gas", "Mains gas"],
              ["lpg", "LPG"],
              ["oil", "Oil"],
              ["electric", "Electricity"],
              ["solid", "Coal or wood"],
              ["biomass", "Biomass"],
              ["none", "No heating"],
            ),
          },
          {
            id: "system_type",
            label: "Type",
            kind: "choice",
            required: true,
            options: opts(
              ["combi", "Combi boiler"],
              ["system", "System boiler"],
              ["regular", "Regular boiler"],
              ["storage", "Storage heaters"],
              ["warm-air", "Warm air"],
              ["other", "Other"],
            ),
          },
          {
            id: "system_age",
            label: "Age",
            kind: "choice",
            required: true,
            options: opts(["pre-1998", "Before 1998"], ["1998-2007", "1998 to 2007"], ["post-2007", "After 2007"], ["unknown", "Don't know"]),
          },
          { id: "heating_location", label: "Where is it?", kind: "text", maxLength: 120 },
          {
            id: "emitters",
            label: "Heats the house through",
            kind: "choice",
            options: opts(["radiators", "Radiators"], ["ufh", "Underfloor"], ["mixed", "Both"], ["none", "Neither"]),
          },
        ],
      },
      {
        title: "What happens to it",
        fields: [
          {
            id: "removal",
            label: "When the heat pump goes in",
            kind: "choice",
            options: opts(["remove", "Take it out"], ["leave", "Leave it as a back-up"], ["cap", "Disconnect and cap"]),
          },
        ],
      },
    ],
  },
  {
    id: "pipework",
    title: "Pipework",
    blurb: "The index circuit — the longest run to the hardest-working radiator — so the pipe sizes and pump can be checked.",
    groups: [
      {
        title: "Primary, heat pump to the first split",
        fields: [
          { id: "primary_pipe", label: "Pipe", kind: "choice", required: true, options: COPPER },
          { id: "primary_length_m", label: "Length, one way", kind: "number", unit: "m", required: true, min: 0.1, max: 200 },
        ],
      },
      {
        title: "On to the index radiator",
        fields: [
          { id: "index_room", label: "The index radiator's room", kind: "text", maxLength: 80 },
          { id: "branch_pipe", label: "Branch pipe", kind: "choice", options: COPPER },
          { id: "branch_length_m", label: "Branch length, one way", kind: "number", unit: "m", min: 0, max: 200 },
          { id: "tail_pipe", label: "Pipe to the radiator itself", kind: "choice", options: COPPER },
          { id: "tail_length_m", label: "Its length, one way", kind: "number", unit: "m", min: 0, max: 100 },
        ],
      },
    ],
  },
  {
    id: "site",
    title: "Site & access",
    blurb: "Getting to the house on the day, and the water supply.",
    groups: [
      {
        title: "The property",
        fields: [
          {
            id: "site_photos",
            label: "Photos of the property",
            kind: "photos",
            required: true,
            photoHints: ["front", "house", "property", "street", "elevation"],
          },
        ],
      },
      {
        title: "Access",
        fields: [
          {
            id: "parking",
            label: "Parking",
            kind: "choice",
            required: true,
            options: opts(["drive", "On the drive"], ["street", "On the street"], ["permit", "Permit needed"], ["none", "Nothing nearby"]),
          },
          yes("delivery_space", "Room to put kit down on delivery day?"),
          { id: "access_notes", label: "Anything the team should know getting in?", kind: "long-text" },
        ],
      },
      {
        title: "Mains water",
        fields: [
          { id: "water_photos", label: "Photos of the stopcock and main", kind: "photos", photoHints: ["stopcock", "stop tap", "water main", "mains"] },
          yes("stopcock_works", "Does the stopcock work?"),
          { id: "water_main_mm", label: "Water main size", kind: "number", unit: "mm", min: 5, max: 100 },
          { id: "mains_flow_lpm", label: "Flow rate", kind: "number", unit: "L/min", min: 0, max: 200 },
          { id: "mains_pressure_bar", label: "Pressure", kind: "number", unit: "bar", min: 0, max: 20 },
        ],
      },
    ],
  },
  {
    id: "energy",
    title: "Energy use",
    blurb: "Prices and use today, for running costs and savings. Optional.",
    groups: [
      {
        title: "Electricity",
        fields: [
          { id: "elec_price_p", label: "Price", kind: "number", unit: "p/kWh", min: 0, max: 200 },
          { id: "mpan", label: "MPAN", kind: "text", maxLength: 21, help: "13 digits, on the electricity bill." },
          { id: "dno", label: "Network operator", kind: "text", maxLength: 80, help: "The DNO whose network feeds this house." },
          yes("smart_meter", "Smart meter fitted?", { required: false }),
        ],
      },
      {
        title: "Heating fuel",
        fields: [
          { id: "fuel_kwh", label: "Used a year", kind: "number", unit: "kWh", min: 0, max: 200_000 },
          { id: "fuel_price_p", label: "Price", kind: "number", unit: "p/kWh", min: 0, max: 200 },
        ],
      },
      {
        title: "Large loads",
        fields: [
          yes("ev_charger", "EV charger, now or planned?", { required: false }),
          yes("battery", "Home battery?", { required: false }),
          yes("solar_pv", "Solar panels?", { required: false }),
          yes("hot_tub", "Hot tub?", { required: false }),
        ],
      },
    ],
  },
  {
    id: "notes",
    title: "Notes",
    blurb: "For the team only — never shared with the customer.",
    groups: [
      {
        title: "Notes",
        fields: [{ id: "notes", label: "Notes for the team", kind: "long-text", maxLength: 8000 }],
      },
    ],
  },
];

export function siteSection(id: SiteSurveySectionId): SiteSection {
  const section = SITE_SURVEY_SECTIONS.find((s) => s.id === id);
  if (!section) throw new Error(`No site survey section "${id}".`);
  return section;
}

export function isSiteSurveySectionId(value: string): value is SiteSurveySectionId {
  return (SITE_SURVEY_SECTION_IDS as readonly string[]).includes(value);
}

export function siteFields(section: SiteSection): readonly SiteField[] {
  return section.groups.flatMap((g) => g.fields);
}

/** Whether the question is asked, given the section's other answers. */
export function isAsked(field: SiteField, answers: SiteAnswers): boolean {
  if (field.unless && answers[field.unless.field] === field.unless.is) return false;
  return !field.when || answers[field.when.field] === field.when.is;
}

function hasAnswer(value: SiteAnswer | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export interface SiteSectionProgress {
  /** Required questions asked and answered. */
  readonly answered: number;
  /** Required questions asked. Zero: the section is optional. */
  readonly required: number;
  /** The unanswered required questions, in the order they are asked. */
  readonly missing: readonly string[];
  readonly complete: boolean;
}

export function siteSectionProgress(section: SiteSection, answers: SiteAnswers): SiteSectionProgress {
  const asked = siteFields(section).filter((f) => f.required && isAsked(f, answers));
  const missing = asked.filter((f) => !hasAnswer(answers[f.id])).map((f) => f.id);
  return {
    answered: asked.length - missing.length,
    required: asked.length,
    missing,
    complete: missing.length === 0,
  };
}

/**
 * One answer in the words a person reads — a choice's label rather than its
 * key, a yes-no as "Yes"/"No", a number with its unit, photographs counted.
 *
 * Null when the question was not answered, which is how a reader tells "nobody
 * said" from "the answer was no": the installer pack prints only the answers
 * that exist rather than a page of "Not recorded".
 */
export function siteAnswerText(field: SiteField, value: SiteAnswer | undefined): string | null {
  if (value === undefined || value === null) return null;
  switch (field.kind) {
    case "choice": {
      const option = (field.options ?? []).find((o) => o.value === value);
      return option?.label ?? String(value);
    }
    case "multi": {
      if (!Array.isArray(value) || value.length === 0) return null;
      const labels = value.map((v) => (field.options ?? []).find((o) => o.value === v)?.label ?? String(v));
      return labels.join(", ");
    }
    case "yes-no":
      return value === true ? "Yes" : value === false ? "No" : null;
    case "number": {
      if (typeof value !== "number") return null;
      return field.unit ? `${value} ${field.unit}` : String(value);
    }
    case "text":
    case "long-text": {
      const text = typeof value === "string" ? value.trim() : "";
      return text.length > 0 ? text : null;
    }
    case "photos": {
      if (!Array.isArray(value) || value.length === 0) return null;
      return value.length === 1 ? "1 photo" : `${value.length} photos`;
    }
    case "circuits": {
      if (!Array.isArray(value) || value.length === 0) return null;
      const circuits = value as readonly Circuit[];
      // Each one as an electrician reads a board: what it feeds and its
      // breaker. The measured load, where somebody read one, in brackets.
      return circuits
        .map((c) => {
          const load = c.loadA !== undefined ? ` (${c.loadA} A measured)` : "";
          return `${c.name || CIRCUIT_KIND_LABELS[c.kind]} ${c.ratingA} A${load}`;
        })
        .join("; ");
    }
  }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Why `value` is not an answer to `field`, or null when it is. `null` itself
 * is never passed here: it means "clear this answer" and is handled by the
 * caller.
 */
export function siteAnswerProblem(field: SiteField, value: unknown): string | null {
  switch (field.kind) {
    case "choice":
      return typeof value === "string" && (field.options ?? []).some((o) => o.value === value)
        ? null
        : `must be one of ${(field.options ?? []).map((o) => `"${o.value}"`).join(", ")}`;
    case "multi": {
      if (!Array.isArray(value)) return "must be a list";
      const allowed = new Set((field.options ?? []).map((o) => o.value));
      if (value.some((v) => typeof v !== "string" || !allowed.has(v))) return "holds a value that is not one of its options";
      if (new Set(value).size !== value.length) return "holds the same option twice";
      return null;
    }
    case "yes-no":
      return typeof value === "boolean" ? null : "must be true or false";
    case "number": {
      if (typeof value !== "number" || !Number.isFinite(value)) return "must be a number";
      if (field.min !== undefined && value < field.min) return `must be at least ${field.min}`;
      if (field.max !== undefined && value > field.max) return `must be at most ${field.max}`;
      return null;
    }
    case "text":
    case "long-text": {
      if (typeof value !== "string") return "must be text";
      const max = field.maxLength ?? (field.kind === "text" ? 200 : 4000);
      return value.length > max ? `must be at most ${max} characters` : null;
    }
    case "photos": {
      if (!Array.isArray(value)) return "must be a list of photo ids";
      if (value.length > 30) return "may hold at most 30 photos";
      if (value.some((v) => typeof v !== "string" || !UUID.test(v))) return "must hold photo ids";
      if (new Set(value).size !== value.length) return "holds the same photo twice";
      return null;
    }
    case "circuits": {
      if (!Array.isArray(value)) return "must be a list of circuits";
      if (value.length > 60) return "may hold at most 60 circuits";
      for (const [i, c] of value.entries()) {
        if (typeof c !== "object" || c === null) return `[${i}] must be a circuit`;
        const circuit = c as Record<string, unknown>;
        const keys = Object.keys(circuit);
        if (keys.some((k) => !["name", "kind", "ratingA", "loadA"].includes(k))) return `[${i}] has a field a circuit does not have`;
        if (typeof circuit["name"] !== "string" || circuit["name"].length > 60) return `[${i}].name must be text of at most 60 characters`;
        if (typeof circuit["kind"] !== "string" || !(CIRCUIT_KINDS as readonly string[]).includes(circuit["kind"])) {
          return `[${i}].kind must be one of ${CIRCUIT_KINDS.join(", ")}`;
        }
        const rating = circuit["ratingA"];
        if (typeof rating !== "number" || !Number.isFinite(rating) || rating <= 0 || rating > 125) return `[${i}].ratingA must be a breaker rating, 1 to 125 A`;
        const load = circuit["loadA"];
        if (load !== undefined && (typeof load !== "number" || !Number.isFinite(load) || load < 0 || load > 125)) {
          return `[${i}].loadA must be 0 to 125 A`;
        }
      }
      return null;
    }
  }
}
